/**
 * Custom-scheme links into the native app (SK-203).
 *
 * These are the page's "Open in Skiffs" buttons — the escape hatch for a tap
 * that did NOT become a universal link. That happens constantly: WhatsApp,
 * Instagram, Facebook and other in-app browsers swallow the tap and render the
 * page themselves, and iOS hands the URL to the app only on a real universal
 * link. When it doesn't, this button is the only way into the app.
 *
 * It had never worked. The pages emitted `skiffs://join/{code}` and
 * `skiffs://user/{id}`, and the app's handler matched `join` only with a
 * `?code=` query — for a custom scheme the first segment parses as the *host*,
 * so `skiffs://join/ABC123` has path `ABC123` and matched nothing at all. The
 * button opened the app to wherever it had last been and dropped the link.
 *
 * The paths below are now IDENTICAL to the https share paths (`/s/{code}`,
 * `/u/{id}`), so one shape reaches the app by either route, and
 * `frontend/src/utils/deepLink.ts` resolves both spellings on one branch.
 * `web/src/lib/__tests__/appLinks.test.ts` asserts that agreement against the
 * app's own resolver rather than restating it.
 */

/** Must match `expo.scheme` in `frontend/app.json`. */
export const APP_SCHEME = 'skiffs';

/** Same normalization the server applies before looking a code up. */
export function sanitizeShortCode(code: string | undefined): string {
  return (code ?? '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
}

/** `skiffs://s/{code}` — the join sheet for a skiff. */
export function skiffAppLink(code: string | undefined): string | null {
  const sanitized = sanitizeShortCode(code);
  return sanitized ? `${APP_SCHEME}://s/${sanitized}` : null;
}

/**
 * `skiffs://s/{code}/m/{messageId}` — the milestone's skiff.
 *
 * The app currently lands on the skiff rather than the individual card, but the
 * message id stays in the URL: it costs nothing, and the alternative (dropping
 * it here) would mean changing this file again when the app learns to anchor.
 */
export function milestoneAppLink(
  code: string | undefined,
  messageId: string | undefined,
): string | null {
  const base = skiffAppLink(code);
  if (!base) return null;
  return messageId ? `${base}/m/${encodeURIComponent(messageId)}` : base;
}

/** `skiffs://u/{userId}` — a public profile. */
export function profileAppLink(userId: string | undefined): string | null {
  return userId ? `${APP_SCHEME}://u/${encodeURIComponent(userId)}` : null;
}
